import cx from 'classnames';
import { Link, NavLink, LinkProps, useNavigate } from 'react-router-dom';

import { t } from '../../i18n';
import { Button, Icon, Text, TextField } from '..';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useAppSelector } from '../../hooks';
import { selectSecurity } from '../../store/securitySlice';
import { Hamburger } from '../Hamburger/Hamburger';
import { setElementHeight } from '../../utils/ElementHeight/elementHeight';

interface HeaderTabProps {
  label: string;
  to: LinkProps['to'];
  renderCondition?: 'isAdmin';
}

export const HeaderTab: React.FC<HeaderTabProps> = (tab) => {
  const security = useAppSelector(selectSecurity).ui;
  const isAdmin = security.isAdmin;

  if (tab.renderCondition === 'isAdmin' && !isAdmin) return null;

  return (
    <NavLink end to={tab.to} className={({ isActive }) => cx('HeaderTab', isActive && 'HeaderTab--active')}>
      {tab.label}
    </NavLink>
  );
};

interface BackLinkProps {
  backlink?: number;
  customBacklink?: never;
  className?: string;
}

const BackLink: React.FC<BackLinkProps> = (props) => {
  const { backlink = -1 } = props;
  const navigate = useNavigate();

  return (
    <div className={cx('HeaderBacklink', props.className)} onClick={() => navigate(backlink)}>
      <Icon color="Primary500" name="arrowLeft" />
    </div>
  );
};

interface CustomBackLinkProps {
  backlink?: never;
  customBacklink: LinkProps['to'];
  className?: string;
}
const CustomBackLink: React.FC<CustomBackLinkProps> = (props) => {
  return (
    <div className={cx('HeaderBacklink', props.className)}>
      <Link to={props.customBacklink}>
        <Icon color="Primary500" name="arrowLeft" />
      </Link>
    </div>
  );
};

interface HeaderProps {
  title?: string;
  actions?: JSX.Element | false;
  secondRow?: JSX.Element;
  afterTitle?: JSX.Element;
  tabs?: HeaderTabProps[];
  search?: {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  };
}

type HeaderP = HeaderProps & (BackLinkProps | CustomBackLinkProps);

export const Header: React.FC<HeaderP> = (props) => {
  const tabs = props?.tabs?.map((tab) => <HeaderTab key={tab.to.toString()} {...tab} />);

  const headerRef = useRef<HTMLDivElement>(null);
  const headerSmallRef = useRef<HTMLDivElement>(null);

  // Víc akcí se pod hamburger breakpointem nevejde vedle sebe (vypadávaly z obrazovky) —
  // sbalí se do tlačítka „Možnosti" s rozbalovacím panelem. Akce přichází jako JSX s podmínkami,
  // proto se počet skutečně vykreslených tlačítek měří v DOM.
  const actionsWrapperRef = useRef<HTMLDivElement>(null);
  const actionsItemsRef = useRef<HTMLDivElement>(null);
  const [actionCount, setActionCount] = useState(0);
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const isActionsCollapsible = actionCount > 1;

  // Záměrně bez deps: akce se mění s každým renderem rodiče; setState jen při změně počtu → bez smyčky.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const count = actionsItemsRef.current?.childElementCount ?? 0;
    if (count !== actionCount) setActionCount(count);
  });

  useEffect(() => {
    if (!isActionsOpen) return;

    const handlePointerDown = (e: MouseEvent | TouchEvent) => {
      if (!actionsWrapperRef.current?.contains(e.target as Node)) setIsActionsOpen(false);
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsActionsOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isActionsOpen]);

  useEffect(() => {
    const handleHeight = () => {
      setElementHeight(headerRef.current, '--header-height');
      setElementHeight(headerSmallRef.current, '--header-sm-height');
    };

    handleHeight();
    window.addEventListener('resize', handleHeight);
    return () => window.removeEventListener('resize', handleHeight);
  }, [headerRef, headerSmallRef]);

  return (
    <>
      <div
        className={cx('Header', props.tabs && 'Header--has-tabs', isActionsOpen && 'Header--actions-open')}
        ref={headerRef}
      >
        <div className="HeaderRow">
          <Hamburger action="open" className="mr-1" />
          {props.backlink && <BackLink backlink={props.backlink} className="HeaderBacklink--lg" />}
          {props.customBacklink && (
            <CustomBackLink customBacklink={props.customBacklink} className="HeaderBacklink--lg" />
          )}

          <div className="HeaderTitle HeaderTitle--lg">
            {props.title && <Text size="h1">{props.title}</Text>}
            {props.search && (
              <div className="HeaderSearch">
                <TextField
                  name="search"
                  value={props.search.value}
                  onChange={props.search.onChange}
                  placeholder={t('Header.Search.placeholder')}
                />
              </div>
            )}
            {props.afterTitle}
          </div>
          {props.actions && (
            <div
              ref={actionsWrapperRef}
              className={cx(
                'HeaderActions',
                isActionsCollapsible && 'HeaderActions--collapsible',
                isActionsOpen && 'HeaderActions--open'
              )}
            >
              {isActionsCollapsible && (
                <Button
                  size="small"
                  title={t('Header.Actions.toggle')}
                  iconAfter={isActionsOpen ? 'arrowUpSmall' : 'arrowDownSmall'}
                  className="HeaderActions__toggle"
                  onClick={() => setIsActionsOpen((open) => !open)}
                />
              )}
              <div className="HeaderActions__items" ref={actionsItemsRef}>
                {props.actions}
              </div>
            </div>
          )}
        </div>
        {props.secondRow && <div className="HeaderRow">{props.secondRow}</div>}
        {tabs && <div className="HeaderRow HeaderRow--tabs">{tabs}</div>}
      </div>
      <div className="Header--sm">
        <div className="HeaderRow">
          {props.backlink && <BackLink backlink={props.backlink} className="HeaderBacklink--sm" />}
          {props.customBacklink && (
            <CustomBackLink customBacklink={props.customBacklink} className="HeaderBacklink--sm" />
          )}
          <div className="HeaderTitle HeaderTitle--sm" ref={headerSmallRef}>
            {props.title && <Text size="h2">{props.title}</Text>}
            {props.afterTitle}
          </div>
        </div>
      </div>
    </>
  );
};
